































































































import {
  PersistedEventInterface,
  CalendarEvent,
  CalendarPeriod,
} from "@/types";
import { Vue, Component, PropSync } from "vue-property-decorator";
import EventPreview from "@/components/arma/events/EventPreview.vue";
import CalendarCard from "@/components/arma/events/CalendarCard.vue";
import moment from "moment-timezone";

@Component({ components: { EventPreview, CalendarCard } })
export default class EventCalendar extends Vue {
  @PropSync("initialEvents")
  public events!: PersistedEventInterface[];
  @PropSync("period")
  public syncPeriod!: CalendarPeriod;

  public focus = "";
  public type: "day" | "week" | "month" = "month";
  public today = this.formatDate(new Date(), false);
  public weekdays = [1, 2, 3, 4, 5, 6, 0];
  public eventHeight = 170;
  public calendarWidth = 0;

  public typeToLabel = {
    month: "Month",
    week: "Week",
    day: "Day",
  };

  public calendarPeriod = this.syncPeriod;

  public start: Start | null = null;
  public end: End | null = null;

  public colors = [
    "#02c39a",
    "#008dd5",
    "#64113f",
    "#7a542e",
    "#fa8334",
    "#df7373",
    "#002642",
    "#2dd881",
  ];

  public colorUnapproved = "#4b4f52";

  get title() {
    const { start, end } = this;
    if (!start || !end) {
      return "";
    }
    const startMonth = this.monthFormatter(start);
    const endMonth = this.monthFormatter(end);
    const suffixMonth = startMonth === endMonth ? "" : endMonth;
    const startYear = start.year;
    const endYear = end.year;
    const suffixYear = startYear === endYear ? "" : endYear;
    const startDay = start.day + this.nth(start.day);
    const endDay = end.day + this.nth(end.day);
    switch (this.type) {
      case "month":
        return `${startMonth} ${startYear}`;
      case "week":
        return `${startMonth} ${startDay} ${startYear} - ${suffixMonth} ${endDay} ${suffixYear}`;
      case "day":
        return `${startMonth} ${startDay} ${startYear}`;
    }
    return "";
  }

  get monthFormatter() {
    return this.calendarInstance.getFormatter({
      timeZone: "UTC",
      month: "long",
    });
  }

  get calendarEvents() {
    return this.mapCalendarEvents(this.events);
  }

  public mounted() {
    const calendar = this.$refs.calendar as Vue & {
      getStartOfWeek: (date: Start | null) => Start;
      getEndOfWeek: (date: End | null) => End;
    };

    const periodStart = calendar.getStartOfWeek(this.start);
    const periodEnd = calendar.getEndOfWeek(this.end);

    this.calendarPeriod = {
      start: moment.utc(periodStart.date).format("YYYY-MM-DD"),
      end: moment.utc(periodEnd.date).format("YYYY-MM-DD"),
    };

    this.calendarInstance.checkChange();
  }

  public viewDay({ date }: { date: string }) {
    this.focus = date;
    this.type = "day";
  }

  public setToday() {
    this.focus = this.today;
  }

  public changePeriod(direction: string) {
    let { start, end } = this.calendarPeriod;
    if (direction === "next") {
      start = moment
        .utc(this.calendarPeriod.start)
        .add(1, "month")
        .format("YYYY-MM-DD");
      end = moment
        .utc(this.calendarPeriod.end)
        .add(1, "month")
        .format("YYYY-MM-DD");
    } else if (direction === "prev") {
      start = moment
        .utc(this.calendarPeriod.start)
        .subtract(1, "month")
        .format("YYYY-MM-DD");
      end = moment
        .utc(this.calendarPeriod.end)
        .subtract(1, "month")
        .format("YYYY-MM-DD");
    }
    this.calendarPeriod = { start, end };
    this.$emit("period", this.calendarPeriod);
  }

  public prev() {
    this.changePeriod("prev");
    this.calendarInstance.prev();
  }

  public next() {
    this.changePeriod("next");
    this.calendarInstance.next();
  }

  public updateRange({ start, end }: { start: Start; end: End }) {
    this.start = start;
    this.end = end;
  }

  public nth(d: number) {
    return d > 3 && d < 21
      ? "th"
      : ["th", "st", "nd", "rd", "th", "th", "th", "th", "th", "th"][d % 10];
  }

  public rnd(a: number, b: number) {
    return Math.floor((b - a + 1) * Math.random()) + a;
  }

  public formatDate(a: Date, withTime: boolean) {
    return withTime
      ? `${a.getFullYear()}-${
          a.getMonth() + 1
        }-${a.getDate()} ${a.getHours()}:${a.getMinutes()}`
      : `${a.getFullYear()}-${a.getMonth() + 1}-${a.getDate()}`;
  }

  get calendarInstance(): Vue & {
    prev: () => void;
    next: () => void;
    checkChange: () => void;
    getFormatter: (format: any) => any;
  } {
    return this.$refs.calendar as Vue & {
      prev: () => void;
      next: () => void;
      checkChange: () => void;
      getFormatter: (format: any) => any;
    };
  }

  public mapCalendarEvents(events: PersistedEventInterface[]) {
    return events.map((event) => {
      const startTime = moment.utc(event.date.date).local().toDate();
      const endTime = new Date(startTime.getTime() + 120 * 60 * 1000);
      let color = this.colorUnapproved;

      if (event.approved) {
        color = this.colors[event.modpack.id % this.colors.length];
      }

      const calendarEvent: CalendarEvent = {
        id: event.id,
        name: event.name,
        start: this.formatDate(startTime, true),
        end: this.formatDate(endTime, true),
        color,
      };

      if (event.image) {
        calendarEvent.image = event.image;
      }

      if (event.ratings && event.ratings.length > 0) {
        const sum = event.ratings.reduce((acc, r) => acc + r.rating, 0);
        calendarEvent.meanRating = sum / event.ratings.length;
      }

      return calendarEvent;
    });
  }

  public getEventByCalendarEvent(
    calendarEvent: CalendarEvent
  ): PersistedEventInterface | undefined {
    return this.events.find((event) => {
      return event.name === calendarEvent.name;
    });
  }

  public getEventColor(calendarEvent: CalendarEvent) {
    return calendarEvent.color;
  }
}

export interface Start {
  date: string;
  time: string;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
  hasDay: boolean;
  hasTime: boolean;
  past: boolean;
  present: boolean;
  future: boolean;
}

export interface End {
  date: string;
  time: string;
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
  hasDay: boolean;
  hasTime: boolean;
  past: boolean;
  present: boolean;
  future: boolean;
}
